import { Component, inject, Injectable } from '@angular/core';
import { FormsModule, ReactiveFormsModule, Validators } from '@angular/forms';
import { MatDialog, MatDialogRef } from '@angular/material/dialog';
import { MatInputModule } from '@angular/material/input';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';
import { MatRadioModule } from '@angular/material/radio';
import { TranslateModule, TranslateService } from '@ngx-translate/core';
import { BaseDialogService, NonNullableConfigData, TilbyDialogContentComponent, TilbyDialogToolbarComponent } from '@tilby/tilby-ui-lib/components/tilby-dialog';
import { restManager } from 'app/ajs-upgraded-providers';
import { lastValueFrom } from 'rxjs';
import { ConfigurationManagerService, ConfigurationPreferences } from 'src/app/core';
import { EInvoiceSetupFormService } from './e-invoice-setup-form.service';
import { MatIconModule } from '@angular/material/icon';
import { MatOptionModule } from '@angular/material/core';
import { AlertDialogService } from '../alert-dialog';
import { MatSelectModule } from '@angular/material/select';
import { EInvoiceDigitalHubFormGroup, EInvoiceFormGroup, EInvoiceFtpFormGroup, EInvoiceMerchantFields } from './e-invoice-setup.model';
import { MatSlideToggleModule } from '@angular/material/slide-toggle';

export enum EInvoiceSetupStep {
  MODE_SELECT = 'mode-select',
  MERCHANT_SETUP = 'merchant-setup',
  DIGITALHUB_SETUP = 'digitalhub-owned-setup',
  FTP_SETUP = 'ftp-setup'
}

export type SendMode = 'digitalHubOwned' | 'ftp' | 'download';

@Component({
  selector: 'app-e-invoice-setup-dialog',
  standalone: true,
  imports: [ 
    ReactiveFormsModule,
    TilbyDialogContentComponent,
    TilbyDialogToolbarComponent,
    TranslateModule,
    MatRadioModule,
    FormsModule,
    MatInputModule,
    MatProgressSpinnerModule,
    MatIconModule,
    MatOptionModule,
    MatSelectModule,
    MatSlideToggleModule
  ],
  providers: [
    EInvoiceSetupFormService
  ],
  templateUrl: './e-invoice-setup-dialog.component.html',
  styleUrl: './e-invoice-setup-dialog.component.scss'
})
export class EInvoiceSetupDialogComponent {

  private readonly matDialogRef = inject(MatDialogRef);
  private restManager = inject(restManager);
  private configManager = inject(ConfigurationManagerService);
  private readonly translateService = inject(TranslateService);
  protected readonly eInvoiceSetupFormService = inject(EInvoiceSetupFormService);
  private readonly alertDialogService = inject(AlertDialogService)

  protected readonly sendModeOptions = [
    {
      value: 'digitalHubOwned',
      title: 'HISTORY.E_INVOICE_SETUP.DIGITAL_HUB_OWNED_TITLE',
      description: 'HISTORY.E_INVOICE_SETUP.DIGITAL_HUB_OWNED_DESCRIPTION',
      priceKey: 'DIGITAL_HUB_OWNED'
    },
    {
      value: 'ftp',
      title: 'HISTORY.E_INVOICE_SETUP.FTP_TITLE',
      description: 'HISTORY.E_INVOICE_SETUP.FTP_DESCRIPTION',
      priceKey: 'FTP'
    },
    {
      value: 'download',
      title: 'HISTORY.E_INVOICE_SETUP.DOWNLOAD_TITLE',
      description: 'HISTORY.E_INVOICE_SETUP.DOWNLOAD_DESCRIPTION',
      priceKey: 'DOWNLOAD'
    }
  ];
  
  protected step = EInvoiceSetupStep.MODE_SELECT;
  protected isWorking = false;
  protected errorMessage: string | null = null;
  
  preferencesData: any; 
  billingData: any;

  protected merchantForm!: EInvoiceFormGroup;
  protected digitalHubForm!: EInvoiceDigitalHubFormGroup;
  protected ftpForm!: EInvoiceFtpFormGroup;

  protected merchantFormReady = this.eInvoiceSetupFormService.merchantFormReady;
  protected digitalHubFormReady = this.eInvoiceSetupFormService.digitalHubFormReady;
  protected ftpFormReady = this.eInvoiceSetupFormService.ftpFormReady;

  protected sendMode: SendMode | null = null;
  protected validatorsRequired = Validators.required;
  protected Validators = Validators;

  async ngOnInit() {
    this.isWorking = true;
    try {
      const [prefs, billing] = await Promise.all([
        this.configManager.getShopPreferences('e_invoice'),
        this.restManager.getOne('billing_data').catch((err: any) => err?.data?.error?.message?.owner_data || {})
      ]);

      this.preferencesData = prefs;
      this.billingData = billing;

      this.initForms(this.preferencesData);
    } catch (error) {
      this.errorMessage = this.translateService.instant('HISTORY.E_INVOICE_SETUP.ERRORS.UNKNOWN_ERROR');
    } finally {
      this.isWorking = false;
    }
  }

  private initForms(prefrence: any) {
    this.eInvoiceSetupFormService.createForms(this.preferencesData);
    this.merchantForm = this.eInvoiceSetupFormService.getMerchantForm();
    this.digitalHubForm = this.eInvoiceSetupFormService.getDigitalHubForm();
    this.ftpForm = this.eInvoiceSetupFormService.getFtpForm();
  }

  getTitle(): string {
    switch (this.step) {
      case EInvoiceSetupStep.MODE_SELECT:
          return 'HISTORY.E_INVOICE_SETUP.MODE_SELECT_TITLE';
      case EInvoiceSetupStep.MERCHANT_SETUP:
          return 'HISTORY.E_INVOICE_SETUP.MERCHANT_SETUP_TITLE';
      case EInvoiceSetupStep.DIGITALHUB_SETUP:
          return 'HISTORY.E_INVOICE_SETUP.DIGITAL_HUB_OWNED.TITLE';
      case EInvoiceSetupStep.FTP_SETUP:
          return 'HISTORY.E_INVOICE_SETUP.FTP.TITLE';
      default:
          return '';
    }
  }

  getSendModePrice(priceKey: string): string {
    if (!this.billingData) return '';
    
    if (this.billingData.reseller_scloby_id) {
      return this.translateService.instant('HISTORY.E_INVOICE_SETUP.RESELLER_PRICE_INFO');
    }
    
    return this.translateService.instant(`HISTORY.E_INVOICE_SETUP.${priceKey}_PRICE`);
  }

  async compileMerchant() {
    const vatCode = this.eInvoiceSetupFormService.vatCode?.value;
    if (!vatCode) return;

    this.isWorking = true;
    try {
      const result = await this.restManager.getOne('validate_vat', vatCode);

      if (!result) {
        this.alertDialogService.openDialog({ data: { messageLabel: this.translateService.instant('SETTINGS.SETTINGS.NO_RESULTS') } });
        return;
      }

      this.merchantForm.patchValue({
        'e_invoice.merchant.tax_code': result.tax_number?.slice(0, 16) || '',
        'e_invoice.merchant.company_name': result.name,
        'e_invoice.merchant.vat_code': result.vat_number,
        'e_invoice.merchant.country': 'IT',
        'e_invoice.merchant.head_office.street': result.address_street,
        'e_invoice.merchant.head_office.number': result.address_number,
        'e_invoice.merchant.head_office.zip': result.address_zip,
        'e_invoice.merchant.head_office.city': result.address_city,
        'e_invoice.merchant.head_office.prov': result.address_prov,
        'e_invoice.merchant.head_office.country': 'IT'
      });
    } catch (error) {
      this.alertDialogService.openDialog({ data: { messageLabel: this.translateService.instant('SETTINGS.SETTINGS.NO_INFO_FOUND') } });
    } finally {
      this.isWorking = false;
    }
  }

  async confirm() {
    this.errorMessage = null;

    switch (this.step) {
      case EInvoiceSetupStep.MODE_SELECT:
        if(this.sendMode === 'digitalHubOwned') {
          this.eInvoiceSetupFormService.invoicePrefix?.setValidators([Validators.required])
        } else this.eInvoiceSetupFormService.invoicePrefix?.setValidators([]);
        this.step = EInvoiceSetupStep.MERCHANT_SETUP;
        break;

      case EInvoiceSetupStep.MERCHANT_SETUP:
        await this.handleMerchantConfirm();
        break;

      case EInvoiceSetupStep.DIGITALHUB_SETUP:
      case EInvoiceSetupStep.FTP_SETUP:
        await this.performFinalSetup();
        break;
    }
  }
 
  isConfirmDisabled(): boolean {
    if (this.isWorking) return true;

    switch (this.step) {
      case EInvoiceSetupStep.MODE_SELECT:
        return !this.sendMode;
      case EInvoiceSetupStep.MERCHANT_SETUP:
        return this.merchantForm?.invalid;
      case EInvoiceSetupStep.DIGITALHUB_SETUP:
        return this.digitalHubForm?.invalid;
      case EInvoiceSetupStep.FTP_SETUP:
        return this.ftpForm?.invalid;
      default:
        return false;
    }
  }

  get isBackStep(): boolean {
    return this.step !== EInvoiceSetupStep.MODE_SELECT;
  }

  getConfirmLabel(): string {
    if (this.step === EInvoiceSetupStep.MODE_SELECT || 
       (this.step === EInvoiceSetupStep.MERCHANT_SETUP && this.sendMode !== 'download')) {
        return 'HISTORY.E_INVOICE_SETUP.CONFIRM_LABEL.NEXT';
    }
    return 'HISTORY.E_INVOICE_SETUP.CONFIRM_LABEL.CONFIRM';
  }

  onBackClick() {
    if (this.isWorking) return;
    this.errorMessage = null;

    if (this.step === EInvoiceSetupStep.MERCHANT_SETUP) {
      this.step = EInvoiceSetupStep.MODE_SELECT;
    } else if (this.step === EInvoiceSetupStep.DIGITALHUB_SETUP || this.step === EInvoiceSetupStep.FTP_SETUP) {
      this.step = EInvoiceSetupStep.MERCHANT_SETUP;
    } else {
      this.matDialogRef.close();
    }
  }

  async handleMerchantConfirm() {
    this.isWorking = true;
    this.errorMessage = null;

    try {
      const currentValues = this.merchantForm.getRawValue();
      const changedPrefs: Record<string, any> = {};

      (Object.keys(currentValues) as (keyof EInvoiceMerchantFields)[]).forEach(key => {
        let newValue = currentValues[key];
        const oldValue = this.preferencesData[key];

        if(key === 'e_invoice.bank_iban') newValue = newValue.replace(/\s+/g, '');

        if (newValue !== oldValue) {
          changedPrefs[key] = newValue;
        }
      });

      for (const [key, value] of Object.entries(changedPrefs)) {
        if (!key.startsWith('_')) {
          await this.configManager.setShopPreferenceSync(key as keyof(ConfigurationPreferences), value);
        }
      }

      this.preferencesData = { ...this.preferencesData, ...changedPrefs };
      this.navigateToNextStep();
    } catch (error) {
      this.errorMessage = this.translateService.instant('HISTORY.E_INVOICE_SETUP.ERRORS.UNKNOWN_ERROR');
    } finally {
      this.isWorking = false;
    }
  }

  private navigateToNextStep() {
    switch (this.sendMode) {
      case 'digitalHubOwned':
        this.step = EInvoiceSetupStep.DIGITALHUB_SETUP;
        break;
      case 'ftp':
        this.step = EInvoiceSetupStep.FTP_SETUP;
        break;
      case 'download':
        this.performFinalSetup();
        break;
    }
  }

  private async performFinalSetup() {
    this.isWorking = true;
    let reqBody: any = {};

    switch (this.sendMode) {
      case 'digitalHubOwned':
        const hubData = this.digitalHubForm.getRawValue();
        reqBody = { send_mode: 'digitalhub', ...hubData };
        break;

      case 'ftp':
        const ftpData = this.ftpForm.getRawValue();
        reqBody = { send_mode: 'ftp', ...ftpData };
        break;

      case 'download':
        reqBody = { send_mode: 'download' };
        break;
    }


    try {
      const result = await this.restManager.post('e_invoice_setup', reqBody);
      this.matDialogRef.close(result);
    } catch (error: any) {
      this.handleSetupError(error);
    } finally {
      this.isWorking = false;
    }
  }

  private handleSetupError(error: any) {
    let errCode = 'UNKNOWN_ERROR';
    let detailedMessage = '';

    if (error?.status === -1) {
      errCode = 'OFFLINE';
    } else if (error?.data?.error?.message?.error_code) {
      errCode = error.data.error.message.error_code;
      detailedMessage = error.data.error.message.response || '';
    }

    const translatedErr = this.translateService.instant('HISTORY.E_INVOICE_SETUP.ERRORS.' + errCode);
    this.errorMessage = detailedMessage ? `${translatedErr}:\n${detailedMessage}` : translatedErr;
  }
}


@Injectable({
  providedIn: "root",
})
export class EInvoiceSetupDialogService extends BaseDialogService {
  private readonly dialogRef = inject(MatDialog);

  public openDialog(config?: NonNullableConfigData<{}>) {
    const configEdited: NonNullableConfigData<{}> = {
      ...config,
      ...this.switchMobileDesktopDimensions({ width: "90vw", maxWidth: "1200px" }, {fullScreenForMobile: true}),
      disableClose: true,
      data: {}
    };

    return lastValueFrom(this.dialogRef.open(EInvoiceSetupDialogComponent, configEdited).afterClosed());
  }
}
